import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './LandingPage.module.css';
import DashboardImage from '../../img/dashboardImg.png';
import Login from './../Account/Login';
import Registry from './../Account/Registry';
import EmailVerification from './../Account/EmailVerification';
import Issue from '../Issues/Issue';
import { getBuildingSubscription } from '../../Fetch/fetchSubscription';
import { isTrialBypassUser } from '../../utils/trialBypass';
import { AlertTriangle, CreditCard, X } from 'lucide-react';

const LandingPage = ({
  setIsLoggedIn,
  setUserData,
  onLoginSuccess,
  isAuthenticated,
  userData,
  subscriptionExpiredMessage
}) => {
  const [currentView, setCurrentView] = useState('main');
  const [scrollY, setScrollY] = useState(0);
  const [showExpiredAlert, setShowExpiredAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [expiredBuildingId, setExpiredBuildingId] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectInitiated = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Check for subscription expired message from App.js
    if (subscriptionExpiredMessage) {
      setShowExpiredAlert(true);
      setAlertMessage(subscriptionExpiredMessage);
    }

    // Check URL parameters for subscription expiry
    const urlParams = new URLSearchParams(location.search);
    const expired = urlParams.get('expired');
    const message = urlParams.get('message');

    if (expired === 'true') {
      setShowExpiredAlert(true);
      setAlertMessage(message || 'תקופת הניסיון הסתיימה. יש לבחור תוכנית תשלום כדי להמשיך.');
    }

    // Check for temporary user from failed login
    const tempUser = localStorage.getItem('tempUser');
    if (tempUser) {
      try {
        const userData = JSON.parse(tempUser);
        if (userData?.selectedBuilding?.buildingId) {
          setShowExpiredAlert(true);
          setAlertMessage('תקופת הניסיון הסתיימה עבור הבניין שלך. יש לבחור תוכנית תשלום כדי להמשיך.');
          setExpiredBuildingId(userData.selectedBuilding.buildingId);
        }
      } catch (error) {
        localStorage.removeItem('tempUser');
      }
    }
  }, [subscriptionExpiredMessage, location]);

  // Fixed redirect logic with ref to prevent infinite loops
  useEffect(() => {
    const checkAuthAndSubscription = async () => {
      if (isAuthenticated && userData) {

        // Whitelisted emails always get in, regardless of trial/subscription status
        if (isTrialBypassUser(userData?.email)) {
          navigate('/app/dashboard', { replace: true });
          return;
        }

        // Check subscription status before redirecting to dashboard
        if (userData.buildingId) {
          try {
            const subscription = await getBuildingSubscription(userData.buildingId);

            let subscriptionData;
            if (subscription?.success && subscription.data) {
              subscriptionData = subscription.data;
            } else if (subscription?.trialEndDate) {
              subscriptionData = subscription;
            }

            if (subscriptionData) {
              const trialEndDate = new Date(subscriptionData.trialEndDate);
              const currentDate = new Date();
              const isExpired = currentDate > trialEndDate;

              if (isExpired) {
                // Don't redirect - let the App.js subscription monitoring handle it
                return;
              }
            }
          } catch (error) {
            // If we can't check subscription, still redirect (assume it's a network issue)
          }
        }

        // Only redirect if subscription is not expired
        navigate('/app/dashboard', { replace: true });
      }
    };

    // Add a small delay to prevent race conditions with App.js
    const timeout = setTimeout(() => {
      checkAuthAndSubscription();
    }, 300);

    return () => clearTimeout(timeout);
  }, [isAuthenticated, userData, navigate]);

  // Clear any authentication state on component mount if needed
  useEffect(() => {
    // If we're on the landing page and there's inconsistent auth state, clear it
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    if ((!storedUser || !storedToken) && (isAuthenticated || userData)) {
      if (setIsLoggedIn) setIsLoggedIn(false);
      if (setUserData) setUserData(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      redirectInitiated.current = false;
      setIsRedirecting(false);
    }
  }, []);

  const showLogin = () => setCurrentView('login');
  const showRegistry = () => setCurrentView('registry');
  const showIssue = () => setCurrentView('issue');
  const showMain = () => {
    setCurrentView('main');
    // Clear subscription alerts when going back to main
    setShowExpiredAlert(false);
    setAlertMessage('');
  };

  // Handle navigation to payment page
  const handleGoToPayment = () => {
    // Check if we have temporary user data
    const tempUser = localStorage.getItem('tempUser');
    let buildingId = expiredBuildingId;

    if (tempUser && !buildingId) {
      try {
        const userData = JSON.parse(tempUser);
        buildingId = userData?.selectedBuilding?.buildingId;
      } catch (error) {
      }
    }

    if (buildingId) {
      // Navigate to SUBSCRIPTION payment page (not regular payments)
      navigate('/subscription-payment', {
        state: {
          buildingId,
          reason: 'trial_expired',
          returnTo: '/app/dashboard',
          tempUser: true
        }
      });
    } else {
      // Fallback: show login page with message
      setCurrentView('login');
      setAlertMessage('יש להתחבר כדי לגשת לדף התשלומים.');
    }
  };

  // Dismiss subscription alert
  const dismissAlert = () => {
    setShowExpiredAlert(false);
    setAlertMessage('');
    setExpiredBuildingId(null);
    localStorage.removeItem('tempUser');
  };

  // Enhanced login success handler
  const handleLoginSuccess = (user, email) => {
    // Clear any temporary data and alerts
    localStorage.removeItem('tempUser');
    setShowExpiredAlert(false);
    setAlertMessage('');
    setExpiredBuildingId(null);
    setIsRedirecting(false);
    redirectInitiated.current = false;

    // Call the original login success handler if provided
    if (onLoginSuccess) {
      onLoginSuccess(user, email);
    }
  };

  // Subscription Expired Alert Component
  const SubscriptionExpiredAlert = () => {
    if (!showExpiredAlert || !alertMessage) return null;

    return (
      <div className={styles.subscriptionAlert}>
        <div className={styles.alertCard}>
          <div className={styles.alertIcon}>
            <AlertTriangle size={24} />
          </div>

          <div className={styles.alertContent}>
            <h3 className={styles.alertTitle}>תקופת הניסיון הסתיימה</h3>
            <p className={styles.alertMessage}>{alertMessage}</p>

            <div className={styles.alertActions}>
              <button
                onClick={handleGoToPayment}
                className={styles.paymentButton}
              >
                <CreditCard size={16} />
                בחר תוכנית תשלום
              </button>

              <button
                onClick={dismissAlert}
                className={styles.dismissButton}
              >
                סגור
              </button>
            </div>
          </div>

          <button
            onClick={dismissAlert}
            className={styles.closeButton}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  };

  // Show loading state during redirect to prevent flash
  if (isRedirecting) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>מפנה לדשבורד...</p>
        </div>
      </div>
    );
  }

  if (currentView !== 'main') {
    return (
      <div className={styles.container}>
        {/* Animated background elements */}
        <div className={styles.backgroundElements}>
          <div className={`${styles.backgroundBlob} ${styles.blob1}`}></div>
          <div className={`${styles.backgroundBlob} ${styles.blob2}`}></div>
          <div className={`${styles.backgroundBlob} ${styles.blob3}`}></div>
        </div>

        {/* Subscription Alert */}
        <SubscriptionExpiredAlert />

        {/* Back button */}
        <button onClick={showMain} className={styles.modernBackButton}>
          <span className={styles.backButtonContent}>
            <svg className={styles.backIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            חזור לעמוד הראשי
          </span>
        </button>

        {/* Component content */}
        <div className={styles.componentWrapper}>
          <div className={styles.componentContainer}>
            {currentView === 'login' && (
              <Login
                setIsLoggedIn={setIsLoggedIn}
                setUserData={setUserData}
                onLoginSuccess={handleLoginSuccess}
                setCurrentPage={setCurrentView}
              />
            )}
            {currentView === 'registry' && (
              <Registry
                setIsLoggedIn={setIsLoggedIn}
                setUserData={setUserData}
                setCurrentPage={setCurrentView}
              />
            )}
            {currentView === 'verification' && (
              <EmailVerification
                email={(() => {
                  try {
                    const pending = localStorage.getItem('pendingVerification');
                    return pending ? JSON.parse(pending).email : '';
                  } catch {
                    return '';
                  }
                })()}
                onVerificationComplete={(userData) => {
                  // Check if this is coming from login flow or new registration
                  const pendingVerification = localStorage.getItem('pendingVerification');
                  let fromLogin = false;
                  
                  try {
                    if (pendingVerification) {
                      const pendingData = JSON.parse(pendingVerification);
                      fromLogin = pendingData.fromLogin === true;
                    }
                  } catch {
                    fromLogin = false;
                  }
                  
                  if (fromLogin) {
                    // Coming from login - just go back to login page
                    setCurrentView('login');
                  } else {
                    // New registration - log them in
                    setIsLoggedIn(true);
                    setUserData(userData);
                    if (onLoginSuccess) {
                      onLoginSuccess();
                    }
                  }
                }}
                onBack={() => setCurrentView('login')}
                setCurrentPage={setCurrentView}
              />
            )}
            {currentView === 'issue' && (
              <Issue />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Animated background elements */}
      <div className={styles.backgroundElements}>
        <div className={`${styles.backgroundBlob} ${styles.blob1}`}></div>
        <div className={`${styles.backgroundBlob} ${styles.blob2}`}></div>
        <div className={`${styles.backgroundBlob} ${styles.blob3}`}></div>
      </div>

      {/* Subscription Alert */}
      <SubscriptionExpiredAlert />

      {/* Navigation */}
      <nav className={styles.modernNavigation}>
        <button onClick={showLogin} className={`${styles.modernNavButton} ${styles.loginButton}`}>
          <span className={styles.navButtonContent}>
            <svg className={styles.navIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
            כניסה
          </span>
        </button>
        <button onClick={showRegistry} className={`${styles.modernNavButton} ${styles.registryButton}`}>
          <span className={styles.navButtonContent}>
            <svg className={styles.navIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            רישום
          </span>
        </button>
        <button onClick={showIssue} className={`${styles.modernNavButton} ${styles.issueButton}`}>
          <span className={styles.navButtonContent}>
            <svg className={styles.navIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            השאר פרטים
          </span>
        </button>
      </nav>

      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1 className={styles.heroTitle}>ברוכים הבאים</h1>
            <div className={styles.heroSubtitles}>
              <p className={styles.heroSubtitle}>מערכת לניהול חכם של ועד בית</p>
              <p className={styles.heroDescription}>הדרך הקלה, המהירה והיעילה ביותר לנהל את הבניין שלך</p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className={styles.ctaButtons}>
            <button onClick={showRegistry} className={styles.primaryCta}>
              <span className={styles.ctaContent}>
                התחל עכשיו - 30 ימי ניסיון
                <svg className={styles.ctaIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </button>
            <button onClick={showLogin} className={styles.secondaryCta}>
              כניסה למשתמשים קיימים
            </button>
          </div>

          {/* Scroll indicator */}
          <div className={styles.scrollIndicator}>
            <svg className={styles.scrollIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.featuresContainer}>
          {/* Section header */}
          <div className={styles.featuresHeader}>
            <h2 className={styles.featuresTitle}>✨ הפיצ'רים המרכזיים</h2>
            <p className={styles.featuresSubtitle}>
              כל מה שאתם צריכים כדי לנהל את הבניין שלכם בצורה חכמה ויעילה
            </p>
          </div>

          {/* Features grid */}
          <div className={styles.featuresGrid}>
            <div className={`${styles.featureCard} ${styles.feature1}`}>
              <div className={styles.featureGradient}></div>
              <div className={styles.featureContent}>
                <div className={styles.featureIcon}>✅</div>
                <h3 className={styles.featureTitle}>ניהול משימות וספקים</h3>
                <p className={styles.featureDescription}>יצירת ומעקב אחר משימות תחזוקה עם שיוך לספקים מורשים.</p>
              </div>
            </div>

            <div className={`${styles.featureCard} ${styles.feature2}`}>
              <div className={styles.featureGradient}></div>
              <div className={styles.featureContent}>
                <div className={styles.featureIcon}>💰</div>
                <h3 className={styles.featureTitle}>ניהול הכנסות וועד הבית</h3>
                <p className={styles.featureDescription}>תיעוד וניהול הכנסות מדיירים, כולל מעקב אחר תשלומים.</p>
              </div>
            </div>

            <div className={`${styles.featureCard} ${styles.feature3}`}>
              <div className={styles.featureGradient}></div>
              <div className={styles.featureContent}>
                <div className={styles.featureIcon}>🔔</div>
                <h3 className={styles.featureTitle}>מערכת התראות חכמה</h3>
                <p className={styles.featureDescription}>קבלת עדכונים בזמן אמת על משימות, תשלומים והודעות.</p>
              </div>
            </div>

            <div className={`${styles.featureCard} ${styles.feature4}`}>
              <div className={styles.featureGradient}></div>
              <div className={styles.featureContent}>
                <div className={styles.featureIcon}>🧑‍🤝‍🧑</div>
                <h3 className={styles.featureTitle}>ניהול דיירים ומשתמשים</h3>
                <p className={styles.featureDescription}>יצירת משתמשים עם הרשאות מותאמות, שמירה על פרטיות ועדכון פרטי הדיירים.</p>
              </div>
            </div>

            <div className={`${styles.featureCard} ${styles.feature5}`}>
              <div className={styles.featureGradient}></div>
              <div className={styles.featureContent}>
                <div className={styles.featureIcon}>📅</div>
                <h3 className={styles.featureTitle}>ניהול אירועים בלוח שנה משולב</h3>
                <p className={styles.featureDescription}>תכנון פגישות, אסיפות דיירים ותזכורות חשובות.</p>
              </div>
            </div>

            <div className={`${styles.featureCard} ${styles.feature6}`}>
              <div className={styles.featureGradient}></div>
              <div className={styles.featureContent}>
                <div className={styles.featureIcon}>✉️</div>
                <h3 className={styles.featureTitle}>שליחת הודעות קבוצתיות לדיירים</h3>
                <p className={styles.featureDescription}>תקשורת פשוטה ואפקטיבית לכלל הבניין – ישירות מהמערכת.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Image showcase section */}
      <section className={styles.imageSection}>
        <div className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            <img
              src={DashboardImage}
              alt="Platform showcase"
              className={styles.mainImage}
            />
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={styles.pricingSection} id="pricing">
        <div className={styles.pricingContainer}>
          <div className={styles.pricingHeader}>
            <h2 className={styles.pricingTitle}>תמחור פשוט ושקוף</h2>
            <p className={styles.pricingSubtitle}>
              בחרו את החבילה שמתאימה לגודל הבניין שלכם
            </p>
          </div>

          <div className={styles.pricingGrid}>
            {/* חבילה לבניינים קטנים */}
            <div className={styles.pricingCard}>
              <div className={styles.pricingPlan}>
                <h3 className={styles.planName}>בניין קטן</h3>
                <div className={styles.priceDisplay}>
                  <span className={styles.priceAmount}>₪50</span>
                  <span className={styles.pricePeriod}>לחודש</span>
                </div>
                <div className={styles.planSize}>עד 16 דירות</div>
                <button onClick={showRegistry} className={styles.ctaButton}>
                  התחל ניסיון חינם
                </button>
              </div>
            </div>

            {/* חבילה לבניינים בינוניים */}
            <div className={styles.pricingCard}>
              <div className={styles.pricingPlan}>
                <h3 className={styles.planName}>בניין בינוני</h3>
                <div className={styles.priceDisplay}>
                  <span className={styles.priceAmount}>₪70</span>
                  <span className={styles.pricePeriod}>לחודש</span>
                </div>
                <div className={styles.planSize}>17-80 דירות</div>
                <button onClick={showRegistry} className={styles.ctaButton}>
                  התחל ניסיון חינם
                </button>
              </div>
            </div>

            {/* חבילה לבניינים גדולים */}
            <div className={styles.pricingCard}>
              <div className={styles.pricingPlan}>
                <h3 className={styles.planName}>בניין גדול</h3>
                <div className={styles.priceDisplay}>
                  <span className={styles.priceAmount}>₪120</span>
                  <span className={styles.pricePeriod}>לחודש</span>
                </div>
                <div className={styles.planSize}>מעל 80 דירות</div>
                <button onClick={showRegistry} className={styles.ctaButton}>
                  התחל ניסיון חינם
                </button>
              </div>
            </div>
          </div>

          {/* רשימת פיצ'רים מרכזית */}
          <div className={styles.centralFeaturesList}>
            <h3 className={styles.centralFeaturesTitle}>כל החבילות כוללות:</h3>
            <div className={styles.featuresGrid}>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                ניהול דיירים ותשלומים
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                ניהול הוצאות והכנסות
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                מערכת הודעות והתראות
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                דוחות וסטטיסטיקות
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                ניהול משימות וספקים
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                לוח שנה ואירועים
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                העלאת קבצים ומסמכים
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                ייצוא לאקסל
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                לוח בקרה חיצוני
              </div>
              <div className={styles.featureItem}>
                <span className={styles.checkIcon}>✓</span>
                תמיכה טכנית מלאה
              </div>
            </div>
          </div>

          <div className={styles.pricingNote}>
            <p>✅ 30 ימי ניסיון חינם לכל החבילות</p>
            <p>✅ ללא דמי הרשמה או עלויות נסתרות</p>
            <p>✅ ביטול בכל עת ללא התחייבות</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.finalCtaSection}>
        <div className={styles.finalCtaContainer}>
          <div className={styles.finalCtaCard}>
            <h2 className={styles.finalCtaTitle}>מוכנים לקחת את הניהול לשלב הבא?</h2>
            <p className={styles.finalCtaDescription}>
              הצטרפו לעשרות ועדים שכבר נהנים ממערכת מתקדמת, תמיכה מלאה, וכלי ניהול שיעשו לכם סדר בבניין.
              הרשמה פשוטה, התחלה מיידית.
            </p>
            <div className={styles.finalCtaButtons}>
              <button onClick={showRegistry} className={styles.finalPrimaryCta}>
                הרשמה עכשיו
              </button>
              <button onClick={showIssue} className={styles.finalSecondaryCta}>
                יש לכם שאלות?
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <p className={styles.footerText}>
            © 2025 מערכת ניהול ועד בית. כל הזכויות שמורות.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;